import React, { useState, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import Modal from '../ui/Modal';
import { bulkUpload } from '../../api/employees';
import {
  Upload, FileText, Download, CheckCircle, XCircle, AlertTriangle,
  Trash2, ChevronDown, ChevronUp,
} from 'lucide-react';

const REQUIRED_COLS = ['name', 'email', 'department', 'ai_tool_name', 'start_date'];
const ALL_COLS = [...REQUIRED_COLS, 'tool_identifier', 'notes'];

const normalizeHeader = (h) => h.trim().toLowerCase().replace(/\s+/g, '_');

const parseFile = (file) => {
  return new Promise((resolve, reject) => {
    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: normalizeHeader,
        complete: (result) => resolve(result.data),
        error: (err) => reject(err),
      });
    } else if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });
          const normalized = raw.map((row) => {
            const r = {};
            Object.entries(row).forEach(([k, v]) => { r[normalizeHeader(k)] = String(v).trim(); });
            return r;
          });
          resolve(normalized);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    } else {
      reject(new Error('Unsupported file type. Please upload a CSV or Excel file.'));
    }
  });
};

const validateRows = (rows) => {
  return rows.map((row, i) => {
    const errors = [];
    const rowNum = i + 2;

    REQUIRED_COLS.forEach((col) => {
      if (!row[col] || !String(row[col]).trim()) {
        errors.push(`"${col}" is required`);
      }
    });

    if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(row.email).trim())) {
      errors.push('Invalid email format');
    }

    if (row.start_date) {
      const d = new Date(row.start_date);
      if (isNaN(d.getTime())) errors.push('Invalid start_date (use YYYY-MM-DD)');
    }

    return { rowNum, row, errors, valid: errors.length === 0 };
  });
};

const downloadTemplate = () => {
  const headers = ['name', 'email', 'department', 'ai_tool_name', 'start_date', 'tool_identifier', 'notes'];
  const sampleRows = [
    ['John Doe', 'john@company.com', 'React JS', 'GitHub Copilot', '2026-02-01', 'john-gh', 'Senior Dev'],
    ['Jane Smith', 'jane@company.com', 'AI/ML', 'GitHub Copilot', '2026-02-01', 'jane-gh', ''],
  ];
  const csvContent = [
    headers.join(','),
    ...sampleRows.map((r) => r.map((v) => `"${v}"`).join(',')),
  ].join('\r\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'employee_bulk_upload_template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const STEPS = { UPLOAD: 'upload', PREVIEW: 'preview', RESULT: 'result' };

const BulkUploadModal = ({ isOpen, onClose, onSaved }) => {
  const [step, setStep] = useState(STEPS.UPLOAD);
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showErrors, setShowErrors] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();

  const reset = () => {
    setStep(STEPS.UPLOAD);
    setFile(null);
    setParsedRows([]);
    setParseError('');
    setLoading(false);
    setResult(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const processFile = useCallback(async (f) => {
    setParseError('');
    setFile(f);
    try {
      const raw = await parseFile(f);
      if (raw.length === 0) { setParseError('The file is empty.'); return; }

      const missingCols = REQUIRED_COLS.filter((c) => !Object.keys(raw[0]).includes(c));
      if (missingCols.length > 0) {
        setParseError(`Missing required columns: ${missingCols.join(', ')}. Please use the template.`);
        return;
      }

      const validated = validateRows(raw);
      setParsedRows(validated);
      setStep(STEPS.PREVIEW);
    } catch (err) {
      setParseError(err.message || 'Failed to parse file.');
    }
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) processFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) processFile(f);
  };

  const handleSubmit = async () => {
    const validRows = parsedRows.filter((r) => r.valid).map((r) => r.row);
    if (validRows.length === 0) { toast.error('No valid rows to import.'); return; }

    setLoading(true);
    try {
      const res = await bulkUpload(validRows);
      setResult(res.data);
      setStep(STEPS.RESULT);
      if (res.data.success > 0) onSaved();
      toast.success(`${res.data.success} employee(s) imported successfully.`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk upload failed.');
    } finally {
      setLoading(false);
    }
  };

  const validCount = parsedRows.filter((r) => r.valid).length;
  const invalidCount = parsedRows.filter((r) => !r.valid).length;

  const renderUploadStep = () => (
    <div className="space-y-4">
      {/* Template download */}
      <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-600" />
          <div>
            <p className="text-sm font-medium text-blue-800">Download the template first</p>
            <p className="text-xs text-blue-500">Fill it in and upload it back here.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={downloadTemplate}
          className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1.5"
        >
          <Download className="w-3.5 h-3.5" />
          Template
        </button>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
        }`}
      >
        <Upload className={`w-8 h-8 mx-auto mb-3 ${dragOver ? 'text-primary-500' : 'text-gray-300'}`} />
        <p className="text-sm font-medium text-gray-600">
          Drag & drop your CSV or Excel file here
        </p>
        <p className="text-xs text-gray-400 mt-1">or click to browse</p>
        <p className="text-xs text-gray-300 mt-3">Supports .csv, .xlsx, .xls</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {parseError && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{parseError}</p>
        </div>
      )}

      {/* Required columns info */}
      <div className="bg-gray-50 rounded-xl p-4">
        <p className="text-xs font-semibold text-gray-500 mb-2">Required columns</p>
        <div className="flex flex-wrap gap-2">
          {REQUIRED_COLS.map((c) => (
            <span key={c} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-xs font-mono text-gray-600">{c}</span>
          ))}
        </div>
        <p className="text-xs font-semibold text-gray-500 mt-3 mb-2">Optional columns</p>
        <div className="flex flex-wrap gap-2">
          {['tool_identifier', 'notes'].map((c) => (
            <span key={c} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-xs font-mono text-gray-400">{c}</span>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={handleClose} className="btn-secondary">Cancel</button>
      </div>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-xl px-4 py-2">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <span className="text-sm font-medium text-green-700">{validCount} valid</span>
        </div>
        {invalidCount > 0 && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-2">
            <XCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm font-medium text-red-700">{invalidCount} invalid (will be skipped)</span>
          </div>
        )}
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 ml-auto">
          <FileText className="w-4 h-4 text-gray-400" />
          <span className="text-xs text-gray-500 truncate max-w-[160px]">{file?.name}</span>
        </div>
      </div>

      {/* Error details (collapsible) */}
      {invalidCount > 0 && (
        <div className="border border-red-100 rounded-xl overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-4 py-3 bg-red-50 text-sm font-medium text-red-700"
            onClick={() => setShowErrors(!showErrors)}
          >
            <span className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {invalidCount} row{invalidCount !== 1 ? 's' : ''} with errors
            </span>
            {showErrors ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showErrors && (
            <div className="divide-y divide-red-50 max-h-40 overflow-y-auto">
              {parsedRows.filter((r) => !r.valid).map((r) => (
                <div key={r.rowNum} className="px-4 py-2.5 bg-white">
                  <span className="text-xs font-semibold text-gray-500 mr-2">Row {r.rowNum}</span>
                  {r.row.email && <span className="text-xs text-gray-400 mr-2">{r.row.email}</span>}
                  <span className="text-xs text-red-600">{r.errors.join('; ')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Data preview table */}
      <div className="border border-gray-100 rounded-xl overflow-hidden">
        <p className="px-4 py-2.5 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-100">
          Preview (first 10 valid rows)
        </p>
        <div className="overflow-x-auto max-h-52">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-3 py-2 font-medium text-gray-500">Row</th>
                {ALL_COLS.map((c) => (
                  <th key={c} className="text-left px-3 py-2 font-medium text-gray-500 whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {parsedRows.filter((r) => r.valid).slice(0, 10).map((r) => (
                <tr key={r.rowNum} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-400">{r.rowNum}</td>
                  {ALL_COLS.map((c) => (
                    <td key={c} className="px-3 py-2 text-gray-700 max-w-[120px] truncate">{r.row[c] || '—'}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {validCount > 10 && (
          <p className="px-4 py-2 text-xs text-gray-400 bg-gray-50 border-t border-gray-100">
            +{validCount - 10} more rows not shown
          </p>
        )}
      </div>

      <div className="flex gap-3">
        <button onClick={reset} className="btn-secondary flex items-center gap-1.5">
          <Trash2 className="w-3.5 h-3.5" /> Start Over
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading || validCount === 0}
          className="btn-primary flex-1"
        >
          {loading
            ? 'Importing...'
            : `Import ${validCount} Employee${validCount !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );

  const renderResultStep = () => (
    <div className="space-y-4 py-2">
      <div className="text-center">
        <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${
          result?.failed === 0 ? 'bg-green-100' : 'bg-yellow-100'
        }`}>
          {result?.failed === 0
            ? <CheckCircle className="w-8 h-8 text-green-500" />
            : <AlertTriangle className="w-8 h-8 text-yellow-500" />}
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Import Complete</h3>
        <p className="text-sm text-gray-500 mt-1">{result?.message}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{result?.success}</p>
          <p className="text-xs text-green-600 mt-1">Successfully imported</p>
        </div>
        <div className={`border rounded-xl p-4 text-center ${
          result?.failed > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'
        }`}>
          <p className={`text-2xl font-bold ${result?.failed > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {result?.failed}
          </p>
          <p className={`text-xs mt-1 ${result?.failed > 0 ? 'text-red-600' : 'text-gray-400'}`}>Failed</p>
        </div>
      </div>

      {result?.errors?.length > 0 && (
        <div className="border border-red-100 rounded-xl overflow-hidden">
          <p className="px-4 py-2.5 text-xs font-semibold text-red-600 bg-red-50">Import Errors</p>
          <div className="divide-y divide-gray-50 max-h-36 overflow-y-auto">
            {result.errors.map((e, i) => (
              <div key={i} className="px-4 py-2.5">
                <span className="text-xs font-semibold text-gray-500 mr-2">Row {e.row}</span>
                {e.email && <span className="text-xs text-gray-400 mr-2">{e.email}</span>}
                <span className="text-xs text-red-600">{e.error}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        {result?.failed > 0 && (
          <button onClick={reset} className="btn-secondary flex-1">Upload Again</button>
        )}
        <button onClick={handleClose} className="btn-primary flex-1">Done</button>
      </div>
    </div>
  );

  const titles = {
    [STEPS.UPLOAD]: 'Bulk Upload Employees',
    [STEPS.PREVIEW]: 'Review & Import',
    [STEPS.RESULT]: 'Import Result',
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={titles[step]} size="xl">
      {step === STEPS.UPLOAD && renderUploadStep()}
      {step === STEPS.PREVIEW && renderPreviewStep()}
      {step === STEPS.RESULT && renderResultStep()}
    </Modal>
  );
};

export default BulkUploadModal;
